import { memo, useMemo, useRef, useState, useEffect } from 'react';
import { ChevronDownIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import {
  AccordionWrapper,
  AccordionHeader,
  AccordionContentContainer,
  AccordionIcon,
} from './EventShareAccordion.styles';
import EventShareAccordionContent from './EventShareAccordionContent';
import EventShareAccordionHeader from './EventShareAccordionHeader';

interface EventShareAccordionProps {
  share: EventShare;
  isOpen: boolean;
  onToggle: () => void;
}

const EventShareAccordion = ({
  share,
  isOpen,
  onToggle,
}: EventShareAccordionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState('0px');

  // Re-render when any mutable fields change (recipients, comments, updated_at)
  const content = useMemo(
    () => <EventShareAccordionContent share={share} />,
    [
      share.updated_at,
      share.recipients,
      share.additional_comments,
      share.share_type,
    ],
  );

  const header = useMemo(
    () => <EventShareAccordionHeader share={share} />,
    [share.updated_at, share.expiry, share.share_type],
  );

  // Recalculate height whenever content changes or accordion opens
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setContentHeight('0px');
    }
  }, [isOpen, share.updated_at, share.recipients, share.additional_comments]);

  return (
    <AccordionWrapper data-name={`event-share-accordion-${share.session_id}`}>
      <AccordionHeader onClick={onToggle}>
        <Box>{header}</Box>
        <AccordionIcon isOpen={isOpen}>
          <ChevronDownIcon width={11} height={11} />
        </AccordionIcon>
      </AccordionHeader>
      <AccordionContentContainer
        ref={contentRef}
        maxHeight={contentHeight}
        isOpen={isOpen}
      >
        {content}
      </AccordionContentContainer>
    </AccordionWrapper>
  );
};

export default memo(EventShareAccordion);
